/**
 * useYamazumiReal — busca carga real por cargo para o modo "Real" do Yamazumi.
 *
 * Desacoplado do YamazumiSimulator para que possa ser reutilizado em outros contextos.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  fetchRealLoadByRole,
  calcEfficiencyKPIs,
  type YamazumiRealLoad,
  type YamazumiEfficiencyKPIs,
} from '../services/yamazumiSyncService';

interface UseYamazumiRealOptions {
  organizationId: string | null | undefined;
  instanceId: string | null | undefined;
  /** Habilitar busca automática ao montar ou quando instanceId/organizationId mudar */
  enabled?: boolean;
}

interface UseYamazumiRealResult {
  /** Carga real por cargo (roleId → load) */
  realLoadByRole: Record<string, YamazumiRealLoad>;
  /** KPIs globais calculados a partir da carga real */
  kpis: YamazumiEfficiencyKPIs | null;
  loading: boolean;
  error: string | null;
  /** Recarregar manualmente */
  refetch: () => void;
}

export function useYamazumiReal({
  organizationId,
  instanceId,
  enabled = true,
}: UseYamazumiRealOptions): UseYamazumiRealResult {
  const [realLoadByRole, setRealLoadByRole] = useState<Record<string, YamazumiRealLoad>>({});
  const [kpis, setKpis] = useState<YamazumiEfficiencyKPIs | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetchToken, setFetchToken] = useState(0);

  const refetch = useCallback(() => setFetchToken((t) => t + 1), []);

  useEffect(() => {
    if (!enabled || !organizationId || !instanceId) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchRealLoadByRole(organizationId, instanceId)
      .then((loads) => {
        if (cancelled) return;
        const byRole: Record<string, YamazumiRealLoad> = {};
        for (const load of loads) byRole[load.roleId] = load;
        setRealLoadByRole(byRole);
        setKpis(calcEfficiencyKPIs(loads));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Erro ao carregar dados reais');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [organizationId, instanceId, enabled, fetchToken]);

  return { realLoadByRole, kpis, loading, error, refetch };
}
